import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo } from "react";
import LinkComponent from "@Components/Link";
import CartLineItem from "@Components/cartLineItem/CartLineItem";
import { DY_PAGE_CONTEXT } from "@Constants/common";
import { setDYContext } from "@Libs/dynamic-yield/dynamicYield";
import { useCurrentPageContextByPath } from "@wff/hooks/useCurrentPageContextByPath";
import { ICartLineItem } from "@wff/interfaces";
import { cartDrawerSelector } from "@wff/store/cartDrawerSlice";
import { cartSliceSelector } from "@wff/store/cartSlice";
import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import { KEYS_CART } from "../translation-keys";
import classes from "./CartDrawerContent.module.scss";

interface CartDrawerContentProps {
  onClose: () => {
    payload: string | undefined;
    type: string;
  };
  productList: ICartLineItem[] | any; // The list of items in cart
  pageTitle: string;
  productSKU: string;
}

const CartDrawerContent = (props: CartDrawerContentProps) => {
  const { onClose, productList, pageTitle, productSKU } = props;
  const { t } = useTranslation("common");
  const { languageCode } = useAppSelector(localeSelector);
  const { open } = useAppSelector(cartDrawerSelector);
  const cartSlice = useAppSelector(cartSliceSelector);
  const currentPageContext = useCurrentPageContextByPath();

  /**
   * Set 'CART' DY-PageContext for Mini-Cart Open
   * Set underline DY-PageContext for Mini-Cart Close
   */
  const currentCartDrawerStatus = open
    ? DY_PAGE_CONTEXT.CART
    : currentPageContext;

  const memoizeSKUs = useMemo(
    () => cartSlice?.lineItems?.map((lineItem: any) => lineItem?.sku),
    [cartSlice]
  );

  const currentPageContextCallback = useCallback(() => {
    let currentPageContextData: any = undefined;

    if (!open) {
      //When the CartDrawer `close` state
      if (currentPageContext === DY_PAGE_CONTEXT.CATEGORY) {
        currentPageContextData = [pageTitle];
      } else if (currentPageContext === DY_PAGE_CONTEXT.PRODUCT) {
        currentPageContextData = [productSKU];
      } else if (currentPageContext === DY_PAGE_CONTEXT.CART) {
        currentPageContextData = memoizeSKUs;
      } else {
        currentPageContextData = undefined;
      }
    } else {
      //When the CartDrawer `open` state
      currentPageContextData = memoizeSKUs;
    }
    setDYContext(currentCartDrawerStatus, languageCode, currentPageContextData);
  }, [
    currentCartDrawerStatus,
    currentPageContext,
    languageCode,
    memoizeSKUs,
    open,
    pageTitle,
    productSKU,
  ]);

  useEffect(() => {
    currentPageContextCallback();
  }, [currentPageContextCallback]);

  const cartItemList = (
    <div id="Cart">
      <div className={classes.cart__items}>
        {productList.map((cartItem: ICartLineItem, index: number) => {
          return (
            <CartLineItem
              key={index}
              item={cartItem}
              isFlyOut={true}
              isReadOnly={false}
            />
          );
        })}
      </div>
    </div>
  );

  const emptyCart = (
    <div className={classes["cart-flyout__empty"]}>
      <p className={classes["cart-flyout__empty-message"]}>
        {t(KEYS_CART.general.empty)}
      </p>
      <LinkComponent href="/">
        <button
          className={`${classes["cart-flyout__empty-button"]} ${classes["action"]} ${classes["action--primary"]}`}
          onClick={onClose}
        >
          {t(KEYS_CART.general.continue_shopping)}
        </button>
      </LinkComponent>
    </div>
  );

  return (
    <div className={classes["cart-flyout__items"]}>
      <div className={classes["cart-flyout__items-inner"]}>
        {(productList.length && cartItemList) || emptyCart}
      </div>
    </div>
  );
};

CartDrawerContent.defaultProps = {
  productList: [],
};

export default CartDrawerContent;
